import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { blogData } from "../Blog/BlogData";
import { Calendar, ChevronRight, ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";

const Blogs = () => {
  // Br and Color Palette extracted directly from the uploaded logo image
  const COLORS = {
    red: "#841619",       // Exact Deep Crimson Red from logo left side
    cream: "#F2EBE1",     // Exact Warm Cream from logo right side
    gold: "#C89B3C",      // Exact Gold from logo text/icon
    textDark: "#1A1A1A",  // Deep Charcoal for readability on Cream
    textLight: "#FFFFFF", // Pure White for readability on Red
  };

  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  // Sort blogs by latest date
  const sortedBlogs = [...blogData].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Pagination logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = sortedBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(sortedBlogs.length / blogsPerPage);

  // Scroll top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  return (
    <div className="min-h-screen font-serif selection:bg-[#841619] selection:text-white" style={{ backgroundColor: COLORS.cream }}>
      
      {/* =========================================
          SPLIT HEADER (Matches Logo 50/50 Layout)
          ========================================= */}
      <div className="w-full flex flex-col lg:flex-row shadow-sm border-b border-black/5 relative z-20">
        
        {/* Left Side: Solid Red */}
        <div 
          className="w-full lg:w-1/2 pt-32 pb-16 px-6 lg:px-16 flex flex-col justify-end lg:items-end text-left lg:text-right relative overflow-hidden"
          style={{ backgroundColor: COLORS.red }}
        >
          {/* Logo Icon Watermark */}
          <div className="absolute -left-10 -bottom-10 opacity-[0.05] pointer-events-none">
            <svg width="300" height="300" viewBox="0 0 100 100" fill={COLORS.gold}>
              <rect x="10" y="10" width="80" height="80" stroke={COLORS.gold} strokeWidth="2" fill="none" />
              <rect x="20" y="20" width="60" height="60" stroke={COLORS.gold} strokeWidth="2" fill="none" />
            </svg>
          </div>

          <div className="relative z-10 max-w-md ml-auto">
            <div className="flex items-center lg:justify-end gap-4 mb-6">
              <div className="w-8 h-[2px] hidden lg:block" style={{ backgroundColor: COLORS.gold }}></div>
              <span className="uppercase tracking-[0.4em] text-xs font-bold" style={{ color: COLORS.gold }}>
                Journal & News
              </span>
              <div className="w-8 h-[2px] lg:hidden" style={{ backgroundColor: COLORS.gold }}></div>
            </div>

            <h2 className="text-5xl md:text-6xl font-bold uppercase tracking-wide leading-tight" style={{ color: COLORS.textLight }}>
              Legal <br />
              <span className="italic font-light" style={{ color: COLORS.gold }}>Insights.</span>
            </h2>
          </div>
        </div>

        {/* Right Side: Solid Cream */}
        <div 
          className="w-full lg:w-1/2 pt-16 pb-16 lg:pt-32 px-6 lg:px-16 flex flex-col justify-end items-start"
          style={{ backgroundColor: COLORS.cream }}
        >
          <div className="max-w-md">
            <p className="text-sm font-sans leading-relaxed text-gray-700 text-justify mb-6">
              Explore our comprehensive collection of legal analyses, case studies and strategic updates. Crafted by the experts at <span className="font-bold" style={{ color: COLORS.red }}>R & P Law Partners</span>, our library equips you with the knowledge needed to navigate complex legal l andscapes.
            </p>
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rotate-45" style={{ backgroundColor: COLORS.red }}></span>
              <span className="w-2 h-2 rotate-45" style={{ backgroundColor: COLORS.gold }}></span>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================
          MAIN CONTENT (Blog Grid on Cream Background)
          ========================================= */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 relative z-10">
        
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="show"
          key={currentPage} 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12"
        >
          {currentBlogs.map((blog) => (
            <motion.div variants={itemVariants} key={blog.id}>
              <Link
                to={`/blogs/${blog.id}`}
                className="group flex flex-col h-full bg-white rounded-none border border-black/10 hover:border-[#C89B3C] shadow-sm hover:shadow-[0_15px_30px_-10px_rgba(0,0,0,0.1)] transition-all duration-500 relative"
              >
                {/* Image Container */}
                <div className="relative h-60 overflow-hidden bg-gray-100">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 grayscale-[30%] group-hover:grayscale-0"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-60 group-hover:opacity-20 transition-opacity duration-500"></div>
                  
                  {/* Category Badge matching the logo colors */}
                  <div 
                    className="absolute top-4 left-4 text-[10px] font-bold px-3 py-1.5 tracking-widest uppercase shadow-md"
                    style={{ backgroundColor: COLORS.red, color: COLORS.textLight }}
                  >
                    {blog.category}
                  </div>
                </div>

                {/* Content Container */}
                <div className="p-8 flex flex-col flex-grow relative bg-white">
                  
                  {/* Date */}
                  <div className="flex items-center gap-2 text-[10px] mb-4 uppercase tracking-widest font-semibold" style={{ color: COLORS.gold }}>
                    <Calendar size={13} />
                    <span className="text-gray-500">
                      {new Date(blog.date).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 
                    className="text-xl font-bold mb-4 leading-snug line-clamp-2 transition-colors duration-300"
                    style={{ color: COLORS.textDark }}
                    onMouseEnter={(e) => e.currentTarget.style.color = COLORS.red}
                    onMouseLeave={(e) => e.currentTarget.style.color = COLORS.textDark}
                  >
                    {blog.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-gray-500 font-sans text-sm mb-8 line-clamp-3 leading-relaxed flex-grow">
                    {blog.excerpt}
                  </p>

                  {/* Interactive Read More Line */}
                  <div className="mt-auto flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-300" style={{ color: COLORS.red }}>
                    <span>Read Article</span>
                    <div className="w-6 h-[1px] transition-all duration-300 group-hover:w-12" style={{ backgroundColor: COLORS.gold }}></div>
                  </div>
                  
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* =========================================
            PAGINATION
            ========================================= */}
        {totalPages > 1 && (
          <div className="mt-24 pt-10 border-t border-black/10 flex justify-center items-center gap-2">
            
            {/* Prev Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`w-10 h-10 flex items-center justify-center rounded-none border transition-all duration-300 ${
                currentPage === 1
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-black/20 text-[#1A1A1A] hover:bg-[#841619] hover:border-[#841619] hover:text-white"
              }`}
            >
              <ChevronLeft size={16} strokeWidth={2} />
            </button>

            {/* Page Numbers */}
            <div className="flex gap-2 mx-4">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                  style={{
                    backgroundColor: currentPage === index + 1 ? COLORS.gold : "transparent",
                    color: currentPage === index + 1 ? COLORS.textLight : COLORS.textDark,
                    borderColor: currentPage === index + 1 ? COLORS.gold : "rgba(0,0,0,0.2)",
                  }}
                  className="w-10 h-10 flex items-center justify-center rounded-none border font-sans text-sm font-bold transition-all duration-300 hover:-translate-y-0.5"
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`w-10 h-10 flex items-center justify-center rounded-none border transition-all duration-300 ${
                currentPage === totalPages
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-black/20 text-[#1A1A1A] hover:bg-[#841619] hover:border-[#841619] hover:text-white"
              }`}
            >
              <ChevronRight size={16} strokeWidth={2} />
            </button>
            
          </div>
        )}

      </div>
    </div>
  );
};

export default Blogs;